<template>
  <v-card variant="flat" class="pa-4 ncrm-pbx-sync">
    <!-- DPIA A5: fără declarația de temei, redarea înregistrărilor este
         refuzată. Cardul este singurul loc din care blocajul se ridică. -->
    <NcrmPbxRecordingBasisCard :account="account" @updated="$emit('basis-updated', $event)" />
    <div class="d-flex flex-wrap align-center ga-3 mb-3">
      <div>
        <strong>{{ $t("ncrm.integrations.pbx.sync.title") }}</strong>
        <div class="text-caption text-medium-emphasis">{{ $t("ncrm.integrations.pbx.sync.subtitle") }}</div>
      </div>
      <v-spacer />
      <v-chip v-if="account && lastSyncStatus !== 'never'" size="small" v-bind="statusMeta(lastSyncStatus)">
        {{ statusLabel(lastSyncStatus) }}
      </v-chip>
      <v-btn color="primary" :disabled="!account" :loading="loading" @click="emitSync">
        <v-icon start>mdi-phone-sync-outline</v-icon>
        {{ $t("ncrm.integrations.pbx.actions.sync") }}
      </v-btn>
    </div>

    <v-alert v-if="displayError" class="mb-3" type="error">{{ displayError }}</v-alert>
    <v-alert v-else-if="persistedSyncError" class="mb-3" type="error">
      {{ $t("ncrm.integrations.pbx.sync.lastError") }}: {{ persistedSyncError }}
    </v-alert>

    <div class="ncrm-pbx-sync__controls">
      <v-select v-model="draft.mode" density="compact" hide-details :items="modeOptions" :label="$t('ncrm.integrations.pbx.sync.window')" />
      <template v-if="draft.mode === 'custom'">
        <NcrmDateTimeField v-model="draft.customStartTime" density="compact" :label="$t('ncrm.integrations.pbx.sync.start')" />
        <NcrmDateTimeField v-model="draft.customEndTime" density="compact" :label="$t('ncrm.integrations.pbx.sync.end')" />
      </template>
    </div>

    <div v-if="result" class="ncrm-pbx-sync__result mt-3">
      <span v-for="metric in resultMetrics" :key="metric.key" class="ncrm-pbx-sync__metric">
        <span class="text-caption text-medium-emphasis">{{ metric.label }}</span>
        <strong>{{ metric.value }}</strong>
      </span>
    </div>

    <div class="ncrm-pbx-sync__meta mt-3">
      <span>{{ $t("ncrm.integrations.pbx.sync.lastStarted") }}: {{ formatDate(account?.last_sync_started_at) }}</span>
      <span>{{ $t("ncrm.integrations.pbx.sync.lastFinished") }}: {{ formatDate(account?.last_sync_finished_at) }}</span>
      <span>{{ $t("ncrm.integrations.pbx.sync.lastCdr") }}: {{ formatDate(account?.last_cdr_synced_at || result?.last_cdr_synced_at) }}</span>
    </div>
  </v-card>
</template>

<script>
import NcrmPbxRecordingBasisCard from "@/components/ncrm/integrations/NcrmPbxRecordingBasisCard.vue";

import {
  buildPbxSyncWindow,
  createPbxSyncDraft,
  formatPbxDateTime,
  normalizePbxSyncResult,
  resolvePbxStatusMeta,
} from "@/Utile/ncrmPbx.mjs";
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";

export default {
  name: "NcrmPbxSyncPanel",
  components: { NcrmDateTimeField, NcrmPbxRecordingBasisCard },
  props: {
    account: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    result: { type: Object, default: null },
    error: { type: String, default: "" },
  },
  emits: ["sync", "basis-updated"],
  data() {
    return {
      draft: createPbxSyncDraft(),
      localError: "",
    };
  },
  computed: {
    displayError() {
      return this.localError || this.error;
    },
    lastSyncStatus() {
      return String(this.account?.last_sync_status || "never").toLowerCase();
    },
    persistedSyncError() {
      return this.lastSyncStatus === "error" ? this.account?.last_sync_error || "" : "";
    },
    modeOptions() {
      return [
        { title: this.$t("ncrm.integrations.pbx.sync.modes.last24h"), value: "last24h" },
        { title: this.$t("ncrm.integrations.pbx.sync.modes.last7d"), value: "last7d" },
        { title: this.$t("ncrm.integrations.pbx.sync.modes.incremental"), value: "incremental" },
        { title: this.$t("ncrm.integrations.pbx.sync.modes.custom"), value: "custom" },
      ];
    },
    normalizedResult() {
      return this.result ? normalizePbxSyncResult(this.result) : null;
    },
    resultMetrics() {
      const result = this.normalizedResult;
      if (!result) return [];
      return ["imported", "matched", "unmatched", "activities", "skipped"].map((key) => ({
        key,
        label: this.$t(`ncrm.integrations.pbx.sync.metrics.${key}`),
        value: result[key],
      }));
    },
  },
  methods: {
    formatDate: formatPbxDateTime,
    statusMeta(status) {
      return resolvePbxStatusMeta(status);
    },
    statusLabel(value) {
      return this.$t(
        `ncrm.integrations.pbx.status.${String(value || "unknown").replace(/\s+/g, "_").toLowerCase()}`,
        String(value || "-")
      );
    },
    emitSync() {
      this.localError = "";
      const payload = buildPbxSyncWindow(this.draft.mode, this.draft);
      if (this.draft.mode === "custom" && (!payload.startTime || !payload.endTime || payload.startTime >= payload.endTime)) {
        this.localError = this.$t("ncrm.integrations.pbx.sync.customError");
        return;
      }
      this.$emit("sync", payload);
    },
  },
};
</script>

<style scoped>
.ncrm-pbx-sync { border: 1px solid var(--ncrm-color-border-default); border-radius: var(--app-radius-base); }
.ncrm-pbx-sync__controls { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); }
.ncrm-pbx-sync__result { display: flex; flex-wrap: wrap; gap: 8px; }
.ncrm-pbx-sync__metric { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border: 1px solid var(--ncrm-color-border-default); border-radius: var(--app-radius-base); background: var(--ncrm-color-hover-soft); }
.ncrm-pbx-sync__meta { display: flex; flex-wrap: wrap; gap: 8px 16px; color: rgba(var(--v-theme-on-surface), 0.62); font-size: 0.8rem; }
</style>
